import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { testimonials } from "@/lib/data";

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const testimonialsContainerRef = useRef<HTMLDivElement>(null);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  // Update carousel position
  useEffect(() => {
    if (testimonialsContainerRef.current) {
      const slideWidth = testimonialsContainerRef.current.clientWidth;
      testimonialsContainerRef.current.style.transform = `translateX(-${currentIndex * slideWidth}px)`;
    }
  }, [currentIndex]);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section id="testimonials" className="py-16 md:py-24 bg-elegant-gradient relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-[rgba(26,54,93,0.05)] rounded-full -ml-32 -mt-32"></div>
      <div className="absolute bottom-0 right-0 w-48 h-48 bg-[rgba(125,155,118,0.05)] rounded-full -mr-24 -mb-24"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-[rgba(125,155,118,0.1)] text-[#7d9b76] rounded-full text-sm font-medium mb-4">Client Success Stories</span>
          <h2 className="section-title">Client Testimonials</h2>
          <div className="elegant-divider"></div>
          <p className="max-w-2xl mx-auto">Hear from businesses who trust SW Accounting with their financial needs.</p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-xl shadow-elegant">
            <div 
              ref={testimonialsContainerRef}
              className="flex transition-transform duration-500" 
            >
              {testimonials.map((testimonial, index) => (
                <div className="min-w-full px-4 py-6" key={index}>
                  <div className="bg-white p-10 rounded-xl shadow-elegant border border-gray-100">
                    <div className="mb-8 text-[#d9b166]">
                      {[...Array(5)].map((_, i) => (
                        <i 
                          key={i} 
                          className={`fas ${i < testimonial.rating ? 'fa-star' : i + 0.5 === testimonial.rating ? 'fa-star-half-alt' : 'fa-star'} text-xl`}
                        ></i>
                      ))}
                    </div>

                    <p className="text-lg italic mb-8 fancy-quote leading-relaxed text-gray-700">
                      {testimonial.quote}
                    </p>

                    <div className="flex items-center">
                      <div className="mr-4 p-1 border-2 border-[#7d9b76] rounded-full">
                        <div className="w-16 h-16 rounded-full bg-[#7d9b76] flex items-center justify-center text-white text-2xl">
                          <i className="fas fa-user"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-serif text-xl font-bold text-[#1a365d]">
                          {testimonial.name}
                        </h3>
                        <p className="text-[#7d9b76] font-medium flex items-center">
                          <i className="fas fa-building mr-2 text-xs"></i>
                          {testimonial.company}
                        </p>
                        <p className="text-gray-500 text-sm">
                          <i className="far fa-calendar-alt mr-1"></i> {testimonial.date}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="absolute -bottom-16 left-0 right-0 flex justify-center items-center space-x-6">
            <button 
              className="bg-white text-[#1a365d] border border-gray-200 rounded-full w-12 h-12 flex items-center justify-center shadow-elegant focus:outline-none hover:bg-[#1a365d] hover:text-white transition-colors duration-300"
              onClick={prevTestimonial}
              aria-label="Previous testimonial"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button 
                  key={index}
                  className={`w-3 h-3 rounded-full ${currentIndex === index ? 'bg-[#7d9b76]' : 'bg-gray-300'} transition-all duration-300`}
                  onClick={() => goToTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                ></button>
              ))}
            </div>

            <button 
              className="bg-white text-[#1a365d] border border-gray-200 rounded-full w-12 h-12 flex items-center justify-center shadow-elegant focus:outline-none hover:bg-[#1a365d] hover:text-white transition-colors duration-300"
              onClick={nextTestimonial}
              aria-label="Next testimonial"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <div className="text-center mt-24">
          <Link 
            href="/testimonials#submit"
            className="inline-flex items-center border-2 border-[#7d9b76] bg-transparent text-[#7d9b76] hover:bg-[#7d9b76] hover:text-white font-bold py-3 px-8 rounded-md transition-all duration-300 shadow-elegant"
          >
            <i className="fas fa-pen-to-square mr-2"></i>
            Submit Your Testimonial
          </Link>
        </div>
      </div>
    </section>
  );
}